import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProjectArtifactsService } from '@inference.engine/workspace-api-client/web';
import { PROJECT_PERMISSIONS } from '@inference.engine/permissions/web';
import { Checkbox, Spinner } from '@monksflow/monks-ui';
import { enqueueSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';

import { PROJECT_ARTIFACT_QUERY_KEYS } from '@/entities/project-artifact';
import { PROJECTS_QUERY_KEYS, type ProjectListItem } from '@/entities/project';
import { workspaceHttp } from '@/lib/api/http-client';
import { ProjectPickerPopover } from '../ProjectPickerPopover';
import { ProjectPickerItem } from './ProjectPickerItem';

const REQUIRED_PERMISSIONS = [PROJECT_PERMISSIONS.ProjectUploadAssets];

interface SaveToProjectPopoverProps {
  children: React.ReactNode;
  /** Stable ID of the resource being saved. Used to reset state when the resource changes. */
  resourceId: string;
  workspaceId: string | null | undefined;
  /** Called when the popover opens for the first time. Must return the minted artifact id. */
  onMintArtifact: () => Promise<string>;
}

/**
 * Public wrapper. All stateful behavior lives in `SaveToProjectPopoverInner`,
 * which is keyed on `resourceId` so the popover remounts (fresh mint / selection)
 * whenever the viewed resource changes. Replaces a `useEffect([resourceId])`
 * reset (see no-use-effect skill, Rule 5).
 */
export function SaveToProjectPopover(props: SaveToProjectPopoverProps) {
  return <SaveToProjectPopoverInner key={props.resourceId} {...props} />;
}

function SaveToProjectPopoverInner({
  children,
  resourceId,
  workspaceId,
  onMintArtifact,
}: SaveToProjectPopoverProps) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(new Set());
  const [isComputingAttachments, setIsComputingAttachments] = useState(false);
  const [mintedArtifactId, setMintedArtifactId] = useState<string | null>(null);
  const [isMinting, setIsMinting] = useState(false);
  const [togglingProjectId, setTogglingProjectId] = useState<string | null>(null);

  // Tracks the currently-viewed resource so an in-flight mint can tell whether
  // the user navigated away before it resolved. Kept as a ref for parity with
  // the pre-refactor guard; the outer key-reset already remounts on resourceId
  // change, but this guards the in-flight promise resolving on the wrong mount.
  const currentResourceIdRef = useRef(resourceId);

  // Mint the artifact on demand (idempotent find-or-create) when the popover
  // opens. Runs after `ProjectPickerPopover` has applied its own open handling.
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen && workspaceId && !mintedArtifactId && !isMinting) {
      const mintingForId = resourceId;
      setIsMinting(true);
      void onMintArtifact()
        .then((newArtifactId) => {
          // Ignore a resolve for a resource the user has since navigated away from.
          if (currentResourceIdRef.current === mintingForId) {
            setMintedArtifactId(newArtifactId);
          }
        })
        .catch(() => {
          enqueueSnackbar(t('ui.saveToProjectPopover.failedToPrepareItem'), {
            variant: 'error',
          });
        })
        .finally(() => setIsMinting(false));
    }
  };

  // Recompute attachment state whenever the minted artifact or projects change.
  // TODO: migrate to TanStack Query per-project checks.
  useEffect(() => {
    const computeAttachments = async () => {
      if (!mintedArtifactId || projects.length === 0) {
        setSelectedProjectIds(new Set());
        return;
      }

      setIsComputingAttachments(true);
      const attachedProjectIds = new Set<string>();

      await Promise.all(
        projects.map(async (project) => {
          // Artifact routes are scoped by platform id; project.id is internal.
          // Drafts have no platform project, so they can't hold artifacts.
          const platformProjectId = project.platformProjectId;
          if (!platformProjectId) return;
          try {
            const idsResponse = await ProjectArtifactsService.listIds({
              client: workspaceHttp,
              path: { platformProjectId },
            });

            const hasArtifact = idsResponse.data?.artifactIds.includes(mintedArtifactId);

            if (hasArtifact) {
              attachedProjectIds.add(platformProjectId);
            }
          } catch {
            // Ignore errors for individual projects
          }
        }),
      );

      setSelectedProjectIds(attachedProjectIds);
      setIsComputingAttachments(false);
    };

    void computeAttachments();
  }, [mintedArtifactId, projects]);

  // `projectId` here is the PLATFORM project id — the id the artifact routes are
  // scoped to, and the key of `selectedProjectIds` / `togglingProjectId`.
  const handleToggleProject = async (projectId: string) => {
    if (!mintedArtifactId || togglingProjectId === projectId) {
      return;
    }

    setTogglingProjectId(projectId);
    try {
      const isCurrentlySelected = selectedProjectIds.has(projectId);
      const project = projects.find((p) => p.platformProjectId === projectId);
      const projectName = project?.title ?? t('ui.saveToProjectPopover.projectFallback');

      if (isCurrentlySelected) {
        // Detach artifact from project
        try {
          const artifactsResponse = await ProjectArtifactsService.list({
            client: workspaceHttp,
            path: { platformProjectId: projectId },
          });

          const projectArtifact = artifactsResponse.data?.items.find(
            (item) => item.artifactId === mintedArtifactId,
          );

          if (projectArtifact?.id) {
            await ProjectArtifactsService.detach({
              client: workspaceHttp,
              path: {
                platformProjectId: projectId,
                projectArtifactId: projectArtifact.id,
              },
            });

            void queryClient.invalidateQueries({
              queryKey: PROJECT_ARTIFACT_QUERY_KEYS.listsForProject(projectId),
            });
            void queryClient.invalidateQueries({
              queryKey: PROJECTS_QUERY_KEYS.list(workspaceId ?? '', undefined),
            });

            setSelectedProjectIds((prev) => {
              const next = new Set(prev);
              next.delete(projectId);
              return next;
            });

            enqueueSnackbar(
              t('ui.saveToProjectPopover.artifactRemovedFromProject', { projectName }),
              {
                variant: 'success',
                autoHideDuration: 3000,
              },
            );
          } else {
            setSelectedProjectIds((prev) => {
              const next = new Set(prev);
              next.delete(projectId);
              return next;
            });
            enqueueSnackbar(t('ui.saveToProjectPopover.artifactIsNoLonger', { projectName }), {
              variant: 'info',
            });
          }
        } catch {
          enqueueSnackbar(t('ui.saveToProjectPopover.failedToRemoveArtifact', { projectName }), {
            variant: 'error',
          });
        }
      } else {
        // Attach artifact to project
        try {
          await ProjectArtifactsService.attach({
            client: workspaceHttp,
            path: { platformProjectId: projectId },
            body: { attachments: [{ artifactId: mintedArtifactId }] },
          });

          void queryClient.invalidateQueries({
            queryKey: PROJECT_ARTIFACT_QUERY_KEYS.listsForProject(projectId),
          });
          void queryClient.invalidateQueries({
            queryKey: PROJECTS_QUERY_KEYS.list(workspaceId ?? '', undefined),
          });

          setSelectedProjectIds((prev) => {
            const next = new Set(prev);
            next.add(projectId);
            return next;
          });

          enqueueSnackbar(t('ui.saveToProjectPopover.artifactSavedToProject', { projectName }), {
            variant: 'success',
            autoHideDuration: 3000,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);

          if (errorMessage.includes('Artifact(s) not accessible') || errorMessage.includes('403')) {
            enqueueSnackbar(t('ui.saveToProjectPopover.unableToSaveTo'), {
              variant: 'error',
            });
          } else {
            enqueueSnackbar(t('ui.saveToProjectPopover.failedToSaveTo'), { variant: 'error' });
          }
        }
      }
    } finally {
      setTogglingProjectId(null);
    }
  };

  return (
    <ProjectPickerPopover
      workspaceId={workspaceId}
      onOpenChange={handleOpenChange}
      onProjectsChange={setProjects}
      additionalLoadingCheck={isComputingAttachments || isMinting}
      includePermissions
      renderProject={(project) => {
        // Selection + toggle are keyed by the platform id (what the artifact
        // routes need). Drafts have none, so they can't receive an artifact.
        const platformProjectId = project.platformProjectId;
        const isSelected = platformProjectId ? selectedProjectIds.has(platformProjectId) : false;
        const isToggling = platformProjectId != null && togglingProjectId === platformProjectId;
        return (
          <ProjectPickerItem
            key={project.id}
            project={project}
            requiredPermissions={REQUIRED_PERMISSIONS}
            deniedMessage={t('ui.saveToProjectPopover.youDonTHavePermission')}
            onClick={() => {
              if (!platformProjectId || isToggling) return;
              void handleToggleProject(platformProjectId);
            }}
            role="checkbox"
            ariaChecked={isSelected}
            preventClose={true}
            trailing={
              isToggling ? (
                <Spinner className="text-muted-foreground" />
              ) : (
                <Checkbox
                  checked={isSelected}
                  disabled={!platformProjectId}
                  className="[&_svg]:text-background!"
                />
              )
            }
          />
        );
      }}
    >
      {children}
    </ProjectPickerPopover>
  );
}
